import '../../../styles/component/asset/asset-inventory.scss';
import server from "../../../server";
import React, {useEffect, useState} from "react";
import {AssetFormData, IInventoryState} from "../asset-category/type/asset-category.types";
import dayjs from "dayjs";
import Locale from "../../../core/locale/locale";
import {Tag, Tooltip} from "antd";
import {removeUnderScore} from "../../../utility/utility";
import AssetInventoryView from "./asset-inventory.view";
import AssetHistoryModal from "./asset-history-modal";
import {SearchParams} from "../../common/search-criteria/type/search-criteria.type";
import {AssetAllocationStatus} from "../../../domains/models/asset-management/asset-allocation";
import {useLocation} from "react-router-dom";
import {Eye} from "lucide-react";

type SearchPayload = {
    code?: string;
    categoryId?: string;
    status?: string;
    brandName?: string;
};

const AssetInventory = () => {
    const [pageNumber, setPageNumber] = useState(0);
    const pageSize = 12;
    const [state, setState] = useState<IInventoryState>({
        data: {
            asset: [],
            history: [],
        },
        historyModal: false
    })
    const [loading, setLoading] = useState(true);
    const [loadHistory, setLoadingHistory] = useState(true);

    const location = useLocation();

    const [navigationStatus, setNavigationStatus] = useState<string | undefined>(
        location.state?.status
    );

    const [searchPayload, setSearchPayload] = useState<SearchPayload>(
        navigationStatus ? {status: navigationStatus} : {}
    );

    useEffect(() => {
        fetchAllAssets(searchPayload, pageNumber);
    }, [pageNumber, searchPayload])

    async function fetchAllAssets(payload: any, pageNumber: number) {
        setLoading(true)
        try {
            const assets = await server.asset.search(pageNumber, pageSize, payload);
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    asset: assets
                }
            }))
        } catch (e) {
            console.log("Error while fetching assets", e);
        } finally {
            setLoading(false)
        }
    }

    async function handleAssetSearch(params: SearchParams) {
        const payload: any = {
            code: params.code,
            categoryId: params.categoryId,
            status: params.status,
            brandName: params.brandName,
        };
        setSearchPayload(payload);
        setPageNumber(0);
    }

    const handleAssetSearchReset = () => {
        setSearchPayload({});
        setNavigationStatus(undefined);
        setPageNumber(0);
    }

    async function handleAssetHistoryModal(record: any) {
        setState((prev) => ({
            ...prev,
            historyModal: true
        }));
        setLoadingHistory(true);
        try {
            const payload = {
                assetId: record.id,
                status: [AssetAllocationStatus.ALLOCATED, AssetAllocationStatus.MISSING, AssetAllocationStatus.RETURNED, AssetAllocationStatus.REQUEST_CANCELLED, AssetAllocationStatus.REQUEST_REJECTED, AssetAllocationStatus.REQUESTED]
            }
            const assetHistory = await server.asset.allocation.search(undefined, undefined, payload);
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    history: assetHistory
                }
            }))
        } catch (e) {
            console.log("Error while fetching asset history", e);
        } finally {
            setLoadingHistory(false);
        }
    }

    const handleAssetHistoryModalClose = () => {
        setState((prev) => ({
            ...prev,
            historyModal: false
        }));
        setState((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                history: []
            }
        }))
    }

    const handleNextPage = () => {
        setPageNumber(prev => prev + 1);
    };

    const handlePrevPage = () => {
        if (pageNumber > 0) {
            setPageNumber(prev => prev - 1);
        }
    };

    const columns = [
        {
            title: "Category Name",
            dataIndex: "assetCategory",
            key: "assetCategory",
            render: (_: any, data: any) => {
                return (data?.assetCategory?.name ? data?.assetCategory?.name : "-")
            }
        },
        {
            title: "Asset Code",
            dataIndex: "assetCode",
            key: "assetCode",
        },
        {
            title: "Brand Name",
            dataIndex: "brandName",
            key: "brandName",
        },
        {
            title: "Purchase Date",
            dataIndex: "purchaseDate",
            key: "purchaseDate",
            render: (_: any, record: AssetFormData) => {
                return record.purchaseDate ? dayjs(record.purchaseDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Purchase Cost",
            dataIndex: "purchaseCost",
            key: "purchaseCost",
        },
        {
            title: "Warranty Date",
            dataIndex: "warrantyDate",
            key: "warrantyDate",
            render: (_: any, record: AssetFormData) => {
                return record.warrantyDate ? dayjs(record.warrantyDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Manufacturing Date ",
            dataIndex: "manufacturingDate",
            key: "manufacturingDate",
            render: (_: any, record: AssetFormData) => {
                return record.manufacturingDate ? dayjs(record.manufacturingDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Returnable",
            dataIndex: "isReturnable",
            key: "isReturnable",
            render: (isReturnable: boolean) => (
                <p>
                    {isReturnable ? "Yes" : "No"}
                </p>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: AssetFormData) => {
                return <Tag className={`asset-status ${record.status}`}>
                    {removeUnderScore(record.status)}
                </Tag>
            }
        },
        {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (_: any, record: any) => {
                return (
                    <div className={'pn-table-actions'}>
                        <Tooltip title={"View Asset History"}>
                            <Eye className={'action-view'}
                                 onClick={() => handleAssetHistoryModal(record)}
                            />
                        </Tooltip>
                    </div>
                )
            }
        },
    ]

    return (
        <div className={'asset-inventory'}>
            <AssetInventoryView
                handleAssetSearch={handleAssetSearch}
                handleAssetSearchReset={handleAssetSearchReset}
                assetData={state.data.asset}
                columns={columns}
                pageNumber={pageNumber}
                pageSize={pageSize}
                handleNextPage={handleNextPage}
                handlePrevPage={handlePrevPage}
                assetStatusFromNavigation={navigationStatus}
                loading={loading}
            />
            <AssetHistoryModal
                loading={loadHistory}
                assetHistory={state.data.history}
                assetHistoryModal={state.historyModal}
                handleAssetHistoryModalClose={handleAssetHistoryModalClose}
            />
        </div>
    )
}

export default AssetInventory;
